/**
 * `window.localStorage` can throw on access in some environments — Safari private
 * mode (SecurityError) and in-app webviews (Instagram / Facebook browsers), which
 * is exactly where our marketing-embed ad traffic lands. When access throws,
 * reads/writes blow up and auth dies (FRONTEND-1J5: `getToken` throwing leaves
 * the user dead in the water).
 *
 * This accessor try/catches every access and, on the first failure, transparently
 * falls back to a module-level in-memory store so auth keeps working for the
 * session. Persistent storage is still used normally wherever it works — the
 * fallback only engages once an access throws (SecurityError / QuotaExceededError).
 */

const memory = new Map<string, string>();
let blocked = false;

export const safeLocalStorage = {
  getItem(key: string): string | null {
    if (!blocked) {
      try {
        return window.localStorage.getItem(key);
      } catch {
        blocked = true;
      }
    }
    return memory.get(key) ?? null;
  },
  setItem(key: string, value: string): void {
    if (!blocked) {
      try {
        window.localStorage.setItem(key, value);
        return;
      } catch {
        blocked = true;
      }
    }
    memory.set(key, value);
  },
  removeItem(key: string): void {
    if (!blocked) {
      try {
        window.localStorage.removeItem(key);
        return;
      } catch {
        blocked = true;
      }
    }
    memory.delete(key);
  },
};
